import Big from 'big.js'
import { isLessThanMinimalRenderableValue } from 'metronome-utils'
import useTranslation from 'next-translate/useTranslation'
import { ReactNode, useContext, useMemo } from 'react'

import useUserSwapFee from '../hooks/useUserSwapFee'

import CryptoCounter from './CryptoCounter'
import { LockedMETContext } from './LockedMetContext'
import SectionSummary from './SectionSummary'

function LockedPositionsSummary() {
  const { t } = useTranslation()
  const { amountLocked, met, esMet, rewardsData } = useContext(LockedMETContext)

  const votingPower = useMemo(
    () =>
      esMet?.balance &&
      amountLocked &&
      Big(amountLocked).gt(0) &&
      Big(esMet.balance).div(amountLocked).toFixed(2),
    [amountLocked, esMet]
  )

  const { defaultSwapFee, userSwapFee } = useUserSwapFee()
  const tradingFeeDiscount = useMemo(
    () =>
      defaultSwapFee &&
      userSwapFee &&
      Big(1).minus(Big(userSwapFee).div(defaultSwapFee)).times(100).toFixed(2),
    [defaultSwapFee, userSwapFee]
  )

  const items: {
    title: string
    value: ReactNode
    tooltip?: string
  }[] = [
    {
      title: t('total-locked'),
      value: amountLocked && met && (
        <CryptoCounter end={amountLocked} suffix={` ${met.symbol}`} />
      )
    },
    {
      title: t('locking-positions.summary.voting-power.title'),
      tooltip: t('locking-positions.summary.voting-power.description'),
      value: votingPower && `${votingPower}x`
    },
    {
      title: t('locking-positions.summary.trading-fee-discount.title'),
      tooltip: t('locking-positions.summary.trading-fee-discount.description'),
      value: tradingFeeDiscount && `${tradingFeeDiscount}%`
    }
  ]

  if (
    process.env.NEXT_PUBLIC_FLAGS_DISPLAY_REWARDS === 'true' &&
    rewardsData?.rewardsPercent
  ) {
    items.push({
      title: t('locking-positions.summary.reward.title'),
      value: `${
        isLessThanMinimalRenderableValue(
          rewardsData.rewardsPercent,
          parseInt(process.env.NEXT_PUBLIC_UI_PERCENT_DECIMALS)
        )
          ? '<'
          : ''
      }${Big(rewardsData.rewardsPercent).toFixed(
        parseInt(process.env.NEXT_PUBLIC_UI_PERCENT_DECIMALS)
      )}%`
    })
  }

  return <SectionSummary items={items} />
}

export default LockedPositionsSummary
